import { Language } from 'providers/translates/enums';
import { isVac } from './is-vac';

/** First-paint and post-Strapi English chrome for Saluting Service (matches VAC Strapi). */
export const VAC_EN_COPY = {
  header: {
    searchQuilts: 'Search Tributes',
    createQuilt: 'Honour a Veteran',
    byline: 'Honouring duty and service to country',
    about: 'About',
    contact: 'Contact Us',
    howToUse: 'How to Use',
    resources: 'Resources',
    signIn: 'Sign In',
    logout: 'Logout',
    myAccount: 'My Account',
  },
  home: {
    title:
      'From a grateful nation, we recognize the contributions of those who dedicated their lives to service.',
    createQuiltText: 'Honour a Veteran, create a tribute',
    searchQuiltText: 'Find a Veteran, share a memory or story',
    resourcesText: 'Find grief support',
  },
  footer: {
    contactUs: 'Contact us',
    privacyNotice: 'Privacy Notice',
    privacyPolicy: 'Privacy Policy',
    donateToday: 'Donate today',
    copyright: 'Copyright 2025 Canadian Virtual Hospice. All rights reserved.',
  },
  signUp: {
    instructionsToConfirmEmail:
      'Once you click the “Create Account” button below you will receive an email from Saluting Service confirming your account. Click the link in the email to complete account set up. If you don’t see an email from Saluting Service in your inbox, check your Junk or Spam folders.',
  },
} as const;

/** First-paint and post-Strapi French chrome for Saluons leur service (matches VAC Strapi). */
export const VAC_FR_COPY = {
  header: {
    searchQuilts: 'Recherche d’hommages',
    createQuilt: 'Honorer un·e vétéran·e',
    byline: 'Hommage pour service rendu à la patrie',
    about: 'À propos',
    contact: 'Nous joindre',
    howToUse: 'Comment procéder',
    resources: 'Ressources',
    signIn: 'Connexion',
    logout: 'Déconnexion',
    myAccount: 'Mon compte',
  },
  home: {
    title: 'Aux personnes qui lui ont consacré leur vie, la patrie reconnaissante.',
    createQuiltText: 'Rendre hommage à une vétérane ou un vétéran, créer un hommage',
    searchQuiltText: 'Trouver une vétérane ou un vétéran, partager un souvenir ou un récit',
    resourcesText: 'Trouver des services d’accompagnement du deuil',
  },
  footer: {
    contactUs: 'Nous joindre',
    privacyNotice: 'Avis de confidentialité',
    privacyPolicy: 'Politique de confidentialité',
    donateToday: 'Faire un don aujourd’hui',
    copyright: '© Portail palliatif canadien, 2025. Tous droits réservés.',
  },
  signUp: {
    instructionsToConfirmEmail:
      'Après avoir cliqué sur le bouton « Créer un compte » ci-dessous, vous recevrez un courriel avec un lien cliquable pour confirmer votre compte. Cliquez sur ce lien pour terminer la création de votre compte. Si vous ne voyez pas ce courriel dans votre boîte de réception, regardez dans votre dossier « Pourriel ».',
  },
} as const;

type VacChrome = {
  header: Record<string, string>;
  home: Record<string, string>;
  footer: Record<string, string>;
  signUp: Record<string, string>;
};

const copyForLang = (lang: string): VacChrome =>
  lang === Language.FRENCH ? VAC_FR_COPY : VAC_EN_COPY;

export const vacHeaderText = (lang: string, key: keyof typeof VAC_EN_COPY.header) =>
  copyForLang(lang).header[key];

export const vacHomeText = (lang: string, key: keyof typeof VAC_EN_COPY.home) =>
  copyForLang(lang).home[key];

export const vacFooterText = (lang: string, key: keyof typeof VAC_EN_COPY.footer) =>
  copyForLang(lang).footer[key];

export const vacSignUpText = (lang: string, key: keyof typeof VAC_EN_COPY.signUp) =>
  copyForLang(lang).signUp[key];

export const vacHeaderLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.header,
  nonVac: string
) => (isVac() ? vacHeaderText(lang, key) : value || nonVac);

export const vacHomeLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.home,
  nonVac: string
) => (isVac() ? vacHomeText(lang, key) : value || nonVac);

export const vacFooterLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.footer,
  nonVac: string
) => (isVac() ? vacFooterText(lang, key) : value || nonVac);

export const vacSignUpLabel = (
  lang: string,
  value: string | undefined,
  key: keyof typeof VAC_EN_COPY.signUp,
  nonVac: string
) => (isVac() ? vacSignUpText(lang, key) : value || nonVac);

export const vacSeed = () => ({
  [Language.ENGLISH]: {
    header: { ...VAC_EN_COPY.header },
    home: { ...VAC_EN_COPY.home },
    footer: { ...VAC_EN_COPY.footer },
    signUp: { ...VAC_EN_COPY.signUp },
  },
  [Language.FRENCH]: {
    header: { ...VAC_FR_COPY.header },
    home: { ...VAC_FR_COPY.home },
    footer: { ...VAC_FR_COPY.footer },
    signUp: { ...VAC_FR_COPY.signUp },
  },
});

/** @deprecated Use vacSeed. */
export const vacEnglishSeed = vacSeed;

// Child text must not be the email. The About renderer replaces this mailto
// with the contact link and also autolinks an email address in the label,
// which printed the address twice.
const VAC_FEEDBACK_MAILTO =
  '<a href="mailto:info@virtualhospice.ca">here</a>';

const FEEDBACK_BY_CONTACTING =
  `Share your feedback, questions, or suggestions by contacting ${VAC_FEEDBACK_MAILTO}`;

/** Replace the retired survey “here” link with the same mailto used on the French about page. */
export const rewriteVacFeedbackSection = (content: string | undefined) => {
  if (!content) {
    return content;
  }

  return content
    .replace(
      /Share your feedback, questions, or suggestions\s*<a\b[^>]*>here<\/a>/i,
      FEEDBACK_BY_CONTACTING
    )
    .replace(
      /Share your feedback, questions, or suggestions\s*\[here\]\([^)]*simplesurvey[^)]*\)/i,
      FEEDBACK_BY_CONTACTING
    )
    .replace(
      /Share your feedback, questions, or suggestions by contacting\s*<a\b[^>]*>\s*info@virtualhospice\.ca\s*<\/a>/i,
      FEEDBACK_BY_CONTACTING
    );
};

const pinLang = (existing: Record<string, any> | undefined, pinned: VacChrome) => {
  const src = existing || {};
  return {
    ...src,
    header: { ...src.header, ...pinned.header },
    home: { ...src.home, ...pinned.home },
    footer: { ...src.footer, ...pinned.footer },
    signUp: { ...src.signUp, ...pinned.signUp },
  };
};

export type TranslatesByLang = Record<string, any>;

/** Keep Saluting Service chrome when Strapi is slow or returns Healing Quilt strings. */
export const applyVacEnglishCopy = (byLang: TranslatesByLang | undefined): TranslatesByLang => {
  if (!isVac()) {
    return byLang || {};
  }

  const seed = vacSeed();
  const src = byLang || {};

  const english: Record<string, any> = pinLang(
    { ...seed[Language.ENGLISH], ...src[Language.ENGLISH] },
    VAC_EN_COPY
  );

  if (english.about?.feedbackAndPrivacySection) {
    english.about = {
      ...english.about,
      feedbackAndPrivacySection: rewriteVacFeedbackSection(english.about.feedbackAndPrivacySection),
    };
  }

  return {
    ...src,
    [Language.ENGLISH]: english,
    [Language.FRENCH]: pinLang(
      { ...seed[Language.FRENCH], ...src[Language.FRENCH] },
      VAC_FR_COPY
    ),
  };
};
